import { Button, FormControlLabel, Modal, Switch } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { InputSection, InputText } from "../style/ProductCreateModal.styled";
import PageToaster from "../toaster/PageToaster";

interface ProductCreateModalProps {
  open: boolean;
  onClose: () => void;
  onProductCreated: () => void;
  product?: any;
}

const ProductCreateModal: React.FC<ProductCreateModalProps> = ({
  open,
  onClose,
  onProductCreated,
  product
}) => {
  const [productName, setProductName] = useState<string>("");
  const [productQuantity, setProductQuantity] = useState<string>(""); // required
  const [productBarcode, setProductBarcode] = useState<string>("");
  const [productUnitPrice, setProductUnitPrice] = useState<string>(""); // required
  const [productBuyingPrice, setProductBuingPrice] = useState<string>(""); // required

  const [productNcm, setProductNcm] = useState<string>("");
  const [productCfop, setProductCfop] = useState<string>("");
  const [productCest, setProductCest] = useState<string>("");
  const [productUnit, setProductUnit] = useState<string>("");
  const [productOrigin, setProductOrigin] = useState<string>(""); // 0–8
  const [productCst, setProductCst] = useState<string>("");
  const [productIcms, setProductIcms] = useState<string>("");
  const [productIpi, setProductIpi] = useState<string>("");
  const [productPis, setProductPis] = useState<string>("");
  const [productCofins, setProductCofins] = useState<string>("");
  const [productLocation, setProductLocation] = useState<string>("");

  const [productMinimumQuantity, setProductMinimumQuantity] =
    useState<string>("");
  const [productAlertActive, setProductAlertActive] =
    useState<boolean>(false);

  const containerId = "toaster-product-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const [isUpdate, setIsUpdate] = useState(false);

  // validação derivada
  const [triedSubmit, setTriedSubmit] = useState(false);
  const isEmpty = (v: string) => v.trim() === "";
  const toNumber = (v: string) => Number(v.replace(",", "."));

  let invalidName = triedSubmit && isEmpty(productName);
  let invalidQty =
    triedSubmit && (isEmpty(productQuantity) || toNumber(productQuantity) <= 0);

  let invalidBar = triedSubmit && isEmpty(productBarcode);
  let invalidBuy =
    triedSubmit &&
    (isEmpty(productBuyingPrice) || toNumber(productBuyingPrice) <= 0);
  let invalidSell =
    triedSubmit &&
    (isEmpty(productUnitPrice) || toNumber(productUnitPrice) <= 0);

  // refs para focar o primeiro campo obrigatório vazio ao tentar enviar
  const nameInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const buyingPriceInputRef = useRef<HTMLInputElement>(null);
  const sellPriceInputRef = useRef<HTMLInputElement>(null);

  const clear = () => {
    setProductName("");
    setProductQuantity("");
    setProductBarcode("");
    setProductUnitPrice("");
    setProductBuingPrice("");
    setProductNcm("");
    setProductCfop("");
    setProductCest("");
    setProductUnit("");
    setProductOrigin("");
    setProductCst("");
    setProductIcms("");
    setProductIpi("");
    setProductPis("");
    setProductCofins("");
    setProductLocation("");
    setProductMinimumQuantity("");
    setProductAlertActive(false);
    setIsUpdate(false);
    setTriedSubmit(false);
  };

  const checkProduct = () => {
    if (product) {
      setProductName(product.product_name ?? "");
      setProductQuantity(
        product.product_quantity === 0 || product.product_quantity
          ? String(product.product_quantity)
          : ""
      );
      setProductBarcode(product.product_barcode ?? "");
      setProductUnitPrice(
        product.product_unit_price === 0 || product.product_unit_price
          ? String(product.product_unit_price)
          : ""
      );
      setProductBuingPrice(
        product.product_buying_price === 0 || product.product_buying_price
          ? String(product.product_buying_price)
          : ""
      );
      setProductNcm(product.product_ncm ?? "");
      setProductCfop(product.product_cfop ?? "");
      setProductCest(product.product_cest ?? "");
      setProductUnit(product.product_unit ?? "");
      setProductOrigin(
        product.product_origin === 0 || product.product_origin
          ? String(product.product_origin)
          : ""
      );
      setProductCst(product.product_cst ?? "");
      setProductIcms(
        product.product_icms === 0 || product.product_icms
          ? String(product.product_icms)
          : ""
      );
      setProductIpi(
        product.product_ipi === 0 || product.product_ipi
          ? String(product.product_ipi)
          : ""
      );
      setProductPis(
        product.product_pis === 0 || product.product_pis
          ? String(product.product_pis)
          : ""
      );
      setProductCofins(
        product.product_cofins === 0 || product.product_cofins
          ? String(product.product_cofins)
          : ""
      );
      setProductLocation(product.product_location ?? "");
      setProductMinimumQuantity(
        product.product_minimum_quantity === 0 ||
          product.product_minimum_quantity
          ? String(product.product_minimum_quantity)
          : ""
      );
      setProductAlertActive(Boolean(Number(product.product_alert_active)));
      setIsUpdate(true);
      setTriedSubmit(false);
    } else {
      clear();
    }
  };

  useEffect(() => {
    checkProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const handleSubmit = async () => {
    // calculado na hora: os `invalid*` de cima só refletem o render
    // anterior, então no primeiro clique (triedSubmit ainda false) eles
    // sempre dão falso e deixavam passar o envio com campos vazios.
    const nameInvalid = isEmpty(productName);
    const qtyInvalid =
      isEmpty(productQuantity) || toNumber(productQuantity) <= 0;
    const barInvalid = isEmpty(productBarcode);
    const buyInvalid =
      isEmpty(productBuyingPrice) || toNumber(productBuyingPrice) <= 0;
    const sellInvalid =
      isEmpty(productUnitPrice) || toNumber(productUnitPrice) <= 0;

    setTriedSubmit(true);

    if (nameInvalid || qtyInvalid || barInvalid || buyInvalid || sellInvalid) {
      autoError("Por favor, preencha os campos obrigatórios.");

      if (nameInvalid) nameInputRef.current?.focus();
      else if (qtyInvalid) quantityInputRef.current?.focus();
      else if (barInvalid) barcodeInputRef.current?.focus();
      else if (buyInvalid) buyingPriceInputRef.current?.focus();
      else if (sellInvalid) sellPriceInputRef.current?.focus();

      return;
    }

    try {
      const payload = {
        product_name: productName.trim(),
        product_quantity: toNumber(productQuantity),
        product_barcode: productBarcode.trim(),
        product_unit_price: toNumber(productUnitPrice),
        product_buying_price: toNumber(productBuyingPrice),
        product_ncm: productNcm.trim(),
        product_cfop: productCfop.trim(),
        product_cest: productCest.trim(),
        product_unit: productUnit.trim(),
        product_origin:
          productOrigin.trim() === "" ? 0 : Number(productOrigin.trim()),
        product_cst: productCst.trim(),
        product_icms: productIcms.trim() === "" ? 0 : toNumber(productIcms),
        product_ipi: productIpi.trim() === "" ? 0 : toNumber(productIpi),
        product_pis: productPis.trim() === "" ? 0 : toNumber(productPis),
        product_cofins:
          productCofins.trim() === "" ? 0 : toNumber(productCofins),
        product_location: productLocation?.toUpperCase()?.trim(),
        product_minimum_quantity:
          productMinimumQuantity.trim() === ""
            ? undefined
            : toNumber(productMinimumQuantity),
        product_alert_active: productAlertActive ? 1 : 0
      };

      if (isUpdate) {
        await api.put(`/products/${product.product_id}`, payload);
        autoSuccess("Produto atualizado com sucesso!");
      } else {
        await api.post("/products", payload);
        autoSuccess("Produto criado com sucesso!");
      }

      clear();
      onProductCreated();
      onClose();
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao salvar produto:", msg, error);
      autoError(`Erro: ${msg}`);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="create-product-modal"
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--text)",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            width: "70vw",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h5
            style={{ color: "#703", position: "absolute", top: 10, right: 10 }}
          >
            Campos com * são obrigatórios
          </h5>
          <h2>{isUpdate ? "Editar Produto" : "Cadastrar Produto"}</h2>

          <InputSection>
            <InputText
              fullWidth
              label="Nome do Produto"
              required
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              margin="normal"
              error={invalidName}
              $invalid={invalidName}
              inputRef={nameInputRef}
            />

            <InputText
              sx={{ width: "50%" }}
              label="Quantidade"
              type="number"
              required
              value={productQuantity}
              onChange={(e) => setProductQuantity(e.target.value)} // <<< string
              margin="normal"
              error={invalidQty}
              $invalid={invalidQty}
              inputProps={{ inputMode: "numeric", min: 1 }}
              inputRef={quantityInputRef}
            />

            <InputText
              fullWidth
              label="Código de Barras"
              required
              value={productBarcode}
              onChange={(e) => setProductBarcode(e.target.value)}
              margin="normal"
              error={invalidBar}
              $invalid={invalidBar}
              inputRef={barcodeInputRef}
            />
          </InputSection>

          <InputSection>
            <InputText
              sx={{ width: "50%" }}
              label="Quantidade mínima"
              type="number"
              value={productMinimumQuantity}
              onChange={(e) => setProductMinimumQuantity(e.target.value)}
              margin="normal"
              inputProps={{ inputMode: "numeric", min: 0 }}
              helperText="Deixe em branco para não controlar estoque mínimo deste produto."
            />

            <FormControlLabel
              sx={{ marginLeft: 1 }}
              control={
                <Switch
                  checked={productAlertActive}
                  onChange={(e) => setProductAlertActive(e.target.checked)}
                />
              }
              label="Alertar quando atingir a quantidade mínima"
            />
          </InputSection>

          <InputSection>
            <InputText
              fullWidth
              label="Preço compra R$"
              type="number"
              required
              value={productBuyingPrice}
              onChange={(e) => setProductBuingPrice(e.target.value)} // <<< string
              margin="normal"
              error={invalidBuy}
              $invalid={invalidBuy}
              inputProps={{ inputMode: "decimal", min: 0.01, step: "any" }}
              inputRef={buyingPriceInputRef}
            />

            <InputText
              fullWidth
              label="Preço venda R$"
              type="number"
              required
              value={productUnitPrice}
              onChange={(e) => setProductUnitPrice(e.target.value)} // <<< string
              margin="normal"
              error={invalidSell}
              $invalid={invalidSell}
              inputProps={{ inputMode: "decimal", min: 0.01, step: "any" }}
              inputRef={sellPriceInputRef}
            />

            <InputText
              fullWidth
              label="NCM"
              value={productNcm}
              onChange={(e) => setProductNcm(e.target.value)}
              error={productNcm !== "" && productNcm.length !== 8}
              margin="normal"
            />
            <InputText
              fullWidth
              label="CFOP"
              value={productCfop}
              onChange={(e) => setProductCfop(e.target.value)}
              error={productCfop !== "" && productCfop.length !== 4}
              margin="normal"
            />
            <InputText
              fullWidth
              label="CEST"
              value={productCest}
              onChange={(e) => setProductCest(e.target.value)}
              error={productCest !== "" && productCest.length !== 7}
              margin="normal"
            />
          </InputSection>

          <InputSection>
            <InputText
              fullWidth
              label="Unidade"
              value={productUnit}
              onChange={(e) => setProductUnit(e.target.value)}
              error={productUnit !== "" && productUnit.length < 1}
              margin="normal"
            />

            <InputText
              fullWidth
              label="Origem (0-8)"
              type="number"
              value={productOrigin}
              onChange={(e) => setProductOrigin(e.target.value)} // string
              error={
                productOrigin !== "" &&
                (Number(productOrigin) < 0 || Number(productOrigin) > 8)
              }
              margin="normal"
            />
            <InputText
              fullWidth
              label="CST/CSOSN"
              value={productCst}
              onChange={(e) => setProductCst(e.target.value)}
              error={productCst !== "" && productCst.length !== 3}
              margin="normal"
            />
            <InputText
              fullWidth
              label="ICMS (%)"
              type="number"
              value={productIcms}
              onChange={(e) => setProductIcms(e.target.value)}
              error={productIcms !== "" && Number(productIcms) < 0}
              margin="normal"
            />
          </InputSection>

          <InputSection>
            <InputText
              fullWidth
              label="IPI (%)"
              type="number"
              value={productIpi}
              onChange={(e) => setProductIpi(e.target.value)}
              error={productIpi !== "" && Number(productIpi) < 0}
              margin="normal"
            />
            <InputText
              fullWidth
              label="PIS (%)"
              type="number"
              value={productPis}
              onChange={(e) => setProductPis(e.target.value)}
              error={productPis !== "" && Number(productPis) < 0}
              margin="normal"
            />
            <InputText
              fullWidth
              label="COFINS (%)"
              type="number"
              value={productCofins}
              onChange={(e) => setProductCofins(e.target.value)}
              error={productCofins !== "" && Number(productCofins) < 0}
              margin="normal"
            />
            <InputText
              fullWidth
              label="Localização"
              value={productLocation}
              onChange={(e) => setProductLocation(e.target.value)}
              margin="normal"
            />
          </InputSection>

          <div style={{ marginTop: 20, textAlign: "right" }}>
            <Button
              onClick={onClose}
              style={{ marginRight: 10 }}
              color="secondary"
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProductCreateModal;
